import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchProjects } from '../api';
import {
  Badge,
  Empty,
  ErrorBox,
  HealthBadge,
  Loading,
  ProgressBar,
  RelativeTime,
} from '../components/ui';

const STATUS_LABEL: Record<string, string> = {
  planning: 'Planificación',
  active: 'Activo',
  blocked: 'Bloqueado',
  paused: 'Pausado',
  maintenance: 'Mantenimiento',
  archived: 'Archivado',
};

export function ProjectsPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const query = useQuery({ queryKey: ['projects'], queryFn: fetchProjects });

  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorBox error={query.error} />;

  const projects = status === '' ? query.data : query.data.filter((p) => p.status === status);
  const statuses = [...new Set(query.data.map((p) => p.status))];

  return (
    <>
      <h1 className="mb-1 text-lg font-semibold">Proyectos</h1>
      <p className="mb-4 text-[12px] text-[var(--color-ink-muted)]">
        {query.data.length} proyectos, {query.data.filter((p) => p.unmanagedRepoCount > 0).length}{' '}
        sin repositorio remoto.
      </p>

      <div className="mb-5 flex flex-wrap gap-1">
        <button
          type="button"
          onClick={() => setParams({})}
          className={`rounded px-2.5 py-1 text-[12px] ${
            status === ''
              ? 'bg-[var(--color-surface-muted)] font-medium'
              : 'text-[var(--color-ink-muted)]'
          }`}
        >
          Todos
        </button>
        {statuses.map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setParams({ status: value })}
            className={`rounded px-2.5 py-1 text-[12px] ${
              status === value
                ? 'bg-[var(--color-surface-muted)] font-medium'
                : 'text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]'
            }`}
          >
            {STATUS_LABEL[value] ?? value}
          </button>
        ))}
      </div>

      {projects.length === 0 ? (
        <Empty>Ningún proyecto con ese estado.</Empty>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {projects.map((project) => (
            <article
              key={project.slug}
              className="rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface-muted)] p-3.5"
            >
              <div className="flex items-baseline justify-between gap-2">
                <Link to={`/projects/${project.slug}`} className="font-semibold hover:underline">
                  {project.name}
                </Link>
                <HealthBadge health={project.health} reasons={project.healthReasons} />
              </div>

              <p className="mt-1 text-[12px] leading-snug text-[var(--color-ink-muted)]">
                {project.repoNames ?? 'sin repositorio'}
              </p>

              <div className="mt-2 flex flex-wrap gap-1">
                <Badge>{STATUS_LABEL[project.status] ?? project.status}</Badge>
                <Badge
                  tone={
                    project.priority === 'P0'
                      ? 'bad'
                      : project.priority === 'P1'
                        ? 'warn'
                        : 'neutral'
                  }
                >
                  {project.priority}
                </Badge>
                {!project.hasCi && project.status !== 'archived' && (
                  <Badge tone="warn">sin CI</Badge>
                )}
                {project.unmanagedRepoCount > 0 && <Badge tone="bad">no está en GitHub</Badge>}
              </div>

              <dl className="mt-3 grid grid-cols-2 gap-y-1 text-[12px]">
                <dt className="text-[var(--color-ink-muted)]">Progreso</dt>
                <dd>
                  <ProgressBar pct={project.progressPct} />
                </dd>
                <dt className="text-[var(--color-ink-muted)]">PRs abiertos</dt>
                <dd className="tabular-nums">
                  {project.prsOpenHuman}
                  {project.prsOpenBot > 0 && (
                    <span className="text-[var(--color-ink-muted)]">
                      {' '}
                      +{project.prsOpenBot} bot
                    </span>
                  )}
                </dd>
                <dt className="text-[var(--color-ink-muted)]">Commits (30 d)</dt>
                <dd className="tabular-nums">{project.commits30d}</dd>
                <dt className="text-[var(--color-ink-muted)]">Actividad</dt>
                <dd>
                  <RelativeTime iso={project.lastActivityAt} />
                </dd>
              </dl>

              {project.healthReasons.length > 0 && (
                <ul className="mt-2.5 space-y-0.5 border-t border-[var(--color-border-subtle)] pt-2 text-[11px] leading-snug text-[var(--color-ink-muted)]">
                  {project.healthReasons.map((reason) => (
                    <li key={reason}>· {reason}</li>
                  ))}
                </ul>
              )}

              {project.productionUrl && (
                <a
                  href={project.productionUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2.5 inline-block text-[12px] underline opacity-70 hover:opacity-100"
                >
                  Abrir producción
                </a>
              )}
            </article>
          ))}
        </div>
      )}
    </>
  );
}
